<template>
  <AdminLayout>
    <Head title="Customer Check-Ins" />

    <template #pageAlerts>
      <ErrorFlash
        v-if="getErrors('customer', checkInForm)"
        :message="getErrors('customer', checkInForm)"
      ></ErrorFlash>
    </template>

    <template #pageTitle>
      <div class="flex justify-between items-center">
        Check-in
        <Menu
          as="div"
          class="relative text-left ml-3 -mt-1 inline-block"
        >
          <div>
            <MenuButton
              class="inline-flex justify-center w-full rounded-full border border-gray-400 shadow-sm px-3 py-1 bg-gray-100 text-sm font-medium text-blue-600"
            >
              {{ currentLocation.name }}
              <ChevronDownIcon
                class="-mr-1 ml-2 h-5 w-5"
                aria-hidden="true"
              />
            </MenuButton>
          </div>

          <transition
            enter-active-class="transition ease-out duration-100"
            enter-from-class="opacity-0 scale-95"
            enter-to-class="opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-from-class="opacity-100 scale-100"
            leave-to-class="opacity-0 scale-95"
          >
            <MenuItems
              class="justify-end origin-top-right absolute right-0 mt-2 w-40 rounded-md shadow-lg bg-gray-100 divide-y divide-gray-400 focus:outline-none z-50"
            >
              <div class="py-1">
                <MenuItem
                  v-slot="{ active }"
                  v-for="location in locations"
                >
                  <a
                    @click="setCurrentLocation(location.id)"
                    href="#"
                    :class="[
                      active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                      'group flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100'
                    ]"
                  >
                    {{ location.name }}
                  </a>
                </MenuItem>
              </div>
            </MenuItems>
          </transition>
        </Menu>
      </div>
    </template>

    <template #headingOptions>
      <button
        @click="openCheckin()"
        type="button"
        class="inline-flex items-center px-4 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-gray-100 bg-blue-600 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600"
      >
        <UserAddIcon
          class="-ml-0.5 mr-2 h-5 w-5"
          aria-hidden="true"
        />
        Check-In
      </button>
      <Menu
        as="div"
        class="relative inline-block text-left ml-3"
      >
        <div>
          <MenuButton
            class="inline-flex justify-center w-full rounded-md border border-gray-400 shadow-sm px-4 py-3 bg-gray-100 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-400 focus:ring-blue-600"
          >
            Actions
            <ChevronDownIcon
              class="-mr-1 ml-2 h-5 w-5"
              aria-hidden="true"
            />
          </MenuButton>
        </div>

        <transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <MenuItems
            class="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-gray-100 ring-1 ring-gray-400 ring-opacity-5 divide-y divide-gray-400 focus:outline-none z-50"
          >
            <div class="py-1">
              <MenuItem v-slot="{ active }">
                <a
                  @click="openCreate()"
                  href="#"
                  :class="[
                    active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                    'group flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100'
                  ]"
                >
                  <ArrowCircleRightIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-100"
                    aria-hidden="true"
                  />
                  Create Customer
                </a>
              </MenuItem>
              <MenuItem>
                <a
                  @click="openRemove()"
                  href="#"
                  :class="[
                    active ? 'bg-gray-400 text-gray-900' : 'text-gray-700',
                    'group flex items-center px-4 py-2 text-sm hover:bg-blue-600 hover:text-gray-100'
                  ]"
                >
                  <MinusCircleIcon
                    class="mr-3 h-5 w-5 text-gray-400 group-hover:text-gray-100"
                    aria-hidden="true"
                  />
                  Remove from Check-In
                </a>
              </MenuItem>
            </div>
          </MenuItems>
        </transition>
      </Menu>
    </template>

    <div class="flex-1 bg-gray-100">
      <data-table
        :items="customers.data"
        :headers="headers"
        :actions="[]"
        :selectable="true"
        v-model="selected"
      >
        <template v-slot:rows="slotRow">
          <index-row
            v-slot:rows="slotRow"
            v-bind="slotRow"
            @remove="confirmCheckOutSelectedCustomer"
            @ready="markReady"
            :show-route="showCustomerRoute"
          />
        </template>
      </data-table>
    </div>

    <Pagination
      :page="customers.meta.current_page"
      :total="customers.meta.total"
      :per-page="customers.meta.per_page"
      :path="customers.meta.path"
      :params="queryParams"
      v-if="customers.meta.last_page > 1"
    />

    <template #modals>
      <CreateModal
        v-model:open="showingCreateModal"
        :action-text="'Save + Check-In'"
        :title-text="defaultCreateDetails?.id ? 'Update Customer' : 'Create Customer'"
        :disable-events="true"
        :medical-sources="medicalSources.data"
        :require-address="$page?.props?.auth?.company?.settings?.require_customer_address || false"
        @created="checkInCreatedCustomer"
        :default-details="defaultCreateDetails"
      />

      <CheckInModal
        v-model:open="showingCheckinModal"
        @create="openCreate"
        :location-id="currentLocation.id"
        :default-details="scannedRecord"
        :default-query="scannedRecordQuery"
      />
    </template>
  </AdminLayout>
</template>

<script>
import { Head, Link, usePage, useForm } from '@inertiajs/vue3'
import AdminLayout from '@/Layouts/AdminLayout.vue'
import { Popover, PopoverButton, PopoverPanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import {
  PlusSmIcon as PlusSmIconSolid,
  DotsHorizontalIcon,
  ChevronDownIcon,
  ArrowCircleRightIcon,
  ExclamationCircleIcon,
  UsersIcon,
  UserAddIcon,
  XCircleIcon,
  MinusCircleIcon,
  CheckCircleIcon
} from '@heroicons/vue/solid'
import { PlusSmIcon as PlusSmIconOutline } from '@heroicons/vue/outline'
import DataTable from '@/Components/Table/CheckinDataTable'
import IndexRow from '@/Pages/Customers/Components/CheckinIndexRow'
import CreateModal from '@/Pages/Customers/Components/CreateModal'
import CheckInModal from '@/Pages/Customers/Components/CheckInModal'
import ErrorFlash from '@/Partials/Alerts/ErrorFlash'
import Pagination from '@/Components/Pagination.vue'
import SelectMenu from '@/Components/Menus/SelectMenu.vue'
import SmallModal from '@/Components/Modals/SmallModal.vue'
import MediumModal from '@/Components/Modals/MediumModal.vue'
import hasAuth from '@/Mixins/hasAuth'
import interactsWithCurrentLocation from '@/Mixins/interactsWithCurrentLocation'
import checksInCustomers from '@/Mixins/checksInCustomers'
import getsErrors from '@/Mixins/getsErrors'
import Echo from 'laravel-echo'
import { useConfirmDialog } from '@/Composables/confirm-dialog'
import { settle } from '@/utils/promises'
import { useEcho, createChannel, ECHO_EVENTS } from '@/echo'

export default {
  mixins: [hasAuth, interactsWithCurrentLocation, checksInCustomers, getsErrors],

  components: {
    AdminLayout,
    Head,
    Link,
    DotsHorizontalIcon,
    ChevronDownIcon,
    ArrowCircleRightIcon,
    PlusSmIconOutline,
    PlusSmIconSolid,
    ExclamationCircleIcon,
    XCircleIcon,
    UsersIcon,
    UserAddIcon,
    MinusCircleIcon,
    CheckCircleIcon,
    DataTable,
    IndexRow,
    Pagination,
    Popover,
    PopoverButton,
    PopoverPanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    SelectMenu,
    SmallModal,
    MediumModal,
    CreateModal,
    CheckInModal,
    ErrorFlash
  },

  props: {
    /**
     * A list of customers
     */
    customers: {
      type: Object,
      default() {
        return {}
      }
    },
    /**
     * A list of simple integration objects that act as medical sources.
     * e.g. [ { id: 1, name: 'metrc' } ]
     */
    medicalSources: {
      type: Object,
      default() {
        return {}
      }
    },
    scannerLocationId: {
      type: Number,
      default: null
    }
  },

  data() {
    return {
      showingCreateModal: false,
      showingCheckinModal: false,
      selectedCheckOutCustomer: null,
      scannedRecord: null,
      defaultCreateDetails: null,
      selected: [],
      markCustomerVisitReady: this.$inertia.form({
        type: 'next_up'
      })
    }
  },

  setup() {
    const { echoClient } = useEcho()
    return {
      echo: echoClient
    }
  },

  created() {
    this.checkInForm.location_id = this.currentLocationId
    this.checkOutForm.location_id = this.currentLocationId
  },

  mounted() {
    this.connectToScanner()
  },

  unmounted() {
    this.echo.leaveAllChannels()
  },

  watch: {
    showingCheckinModal(newVal, oldVal) {
      if (!newVal) {
        this.scannedRecord = null
      }
    },
    showingCreateModal(newVal, oldVal) {
      if (!newVal) {
        this.defaultCreateDetails = null
      }
    },
    scannerLocationId(newVal, oldVal) {
      console.log('Scanner location changed', newVal, oldVal)
      this.connectToScanner()
    }
  },

  computed: {
    headers() {
      return ['Guest', 'Time In', 'Patient #', 'Remaining', 'Status', 'Veteran', 'Ind. Dis.', 'Loyalty', 'Remove', '']
    },
    showCustomerRoute() {
      return (item) => {
        return route('customers.edit', { customer: item.id })
      }
    },
    newCustomer() {
      return this.$page.props.flash.customer || null
    },
    /**
     * The query from the scannedRecord object if present. Passed to check-in modal.
     */
    scannedRecordQuery() {
      if (this.scannedRecord?.email) {
        return this.scannedRecord.email
      }

      if (this.scannedRecord?.first_name && this.scannedRecord?.last_name) {
        return `${this.scannedRecord.first_name} ${this.scannedRecord.last_name}`
      }

      if (this.scannedRecord?.phone) {
        return this.scannedRecord.phone
      }

      return null
    }
  },

  methods: {
    connectToScanner() {
      this.echo.leaveAllChannels()

      if (!this.scannerLocationId) {
        return
      }

      const CHANNEL_NAME = createChannel('scanRecords', this.scannerLocationId)
      const channel = this.echo.private(CHANNEL_NAME)

      channel.listen(ECHO_EVENTS.SCAN_RECORD_CREATED, this.handleScannedRecord)
    },
    async openRemove() {
      const { show } = useConfirmDialog()
      const [error, data] = await settle(
        show({
          title: 'Removing Customers from Check-In',
          body: 'Are you sure you want to remove these customers from the check-in list?',
          items: this.selected.map((customer) => customer.name)
        })
      )
      if (!error) {
        this.checkoutSelectedCustomers()
      }
    },
    openCreate(customer = null) {
      if (this.scannedRecord) {
        this.defaultCreateDetails = this.scannedRecord
      }

      if (customer) {
        this.defaultCreateDetails = customer
      }

      this.showingCheckinModal = false

      setTimeout(() => (this.showingCreateModal = true), 400)
    },
    closeCreate() {
      this.showingCreateModal = false
    },
    openCheckin() {
      this.showingCheckinModal = true
    },
    closeCheckin() {
      this.showingCheckinModal = false
    },
    setCheckInFormsWithCurrent() {
      this.checkInForm.location_id = this.currentLocationId
      this.checkOutForm.location_id = this.currentLocationId
    },
    checkInCreatedCustomer() {
      this.closeCreate()
      this.setCheckInFormsWithCurrent()

      // todo: open a checking-in spinner?
      this.checkInCustomer(this.newCustomer.id, () => {
        // ok
      })
    },
    async confirmCheckOutSelectedCustomer(customer) {
      this.selectedCheckOutCustomer = customer
      const { show } = useConfirmDialog()
      const [error, data] = await settle(
        show({
          title: 'Removing Customers from Check-In',
          body: 'Are you sure you want to remove the following customer?',
          items: [this.selectedCheckOutCustomer.name]
        })
      )
      if (!error) {
        this.checkOutSelectedCustomer(this.selectedCheckOutCustomer)
      }
      this.selectedCheckOutCustomer = null
    },
    checkOutSelectedCustomer(customer) {
      this.setCheckInFormsWithCurrent()

      this.checkOutCustomer(customer.id, () => {
        // do nothing
      })
    },
    checkoutSelectedCustomers() {
      const customerIds = this.selected.map((customer) => customer.id)

      this.setCheckInFormsWithCurrent()

      this.checkOutCustomers(customerIds, () => {
        this.selected = []
      })
    },
    markReady(item) {
      this.markCustomerVisitReady.post(route('customer-visits.ready', { visit: item.check_in_id }))
    },
    handleScannedRecord(record) {
      console.log('Scanned Record Received', record)

      if (this.scannedRecord) {
        console.log('Scanned Record already in use', this.scannedRecord)
        return
      }

      this.scannedRecord = record

      this.openCheckin()
    }
  }
}
</script>
